import {
    getSelectedAttributes,
    gtmAdpageItemList,
    gtmPushAllListerProducts,
    navigation,
    typedBoolean,
} from "@jog/shared"
import { NavigationResponse } from "@jog/types"
import { useSession } from "next-auth/react"
import { useRouter } from "next/router"
import { useCallback, useEffect, useMemo, useState } from "react"

import { useNavigation } from "./useNavigation"

export const useLister = (path: string, categoryId: string, facets: Record<string, string>) => {
    const router = useRouter()
    const [isRedirect, setIsRedirect] = useState(false)
    const { pending, response, navigation: fetch, loaded, getGroup } = useNavigation(navigation)
    useEffect(() => {
        if (response?.items && response.items.length) {
            gtmAdpageItemList(response.items)
            gtmPushAllListerProducts(response.items)
        }
    }, [response])
    const pushHistoryState = useCallback(
        (res?: NavigationResponse) => {
            if (!res) return
            const pageurl = res.properties.pageurl
            if (!pageurl) return
            const { facets, query } = Object.entries(
                Object.fromEntries(new URLSearchParams(pageurl).entries())
            ).reduce<{
                facets: string[]
                query: URLSearchParams
            }>(
                (pre, [key, value]) => {
                    switch (key) {
                        case "tn_cid":
                        case "tn_sort": {
                            return pre
                        }
                        case "tn_ps": {
                            pre.query.append(key, encodeURIComponent(value))
                            return pre
                        }
                        default: {
                            const str = `${key.replace(/^tn_fk_/, "")}_${value.replace(/ /g, "_").replace(/\|/g, "-")}`
                            return { ...pre, facets: [...pre.facets, str] }
                        }
                    }
                },
                { facets: [], query: new URLSearchParams() }
            )
            facets.sort((a, b) => a.localeCompare(b))
            const prettyUrl = `${path}/${facets.join("/")}?${query}`
                .toLowerCase()
                .replace(/\/\//, "/")
                .replace(/\?$/, "")
            const temp = new URL(window.location.origin + prettyUrl)
            const currentQs = new URLSearchParams(window.location.search)
            const qs = new URLSearchParams(temp.searchParams)
            currentQs.forEach((value, key) => {
                if (["tn_p", "tn_sort"].includes(key)) return
                if (!qs.has(key)) qs.append(key, value)
            })
            const tn_sort = res.properties.sortfields?.find((i) => i.isselected)?.title
            if (tn_sort) qs.append("tn_sort", tn_sort)
            const tn_p = res.properties.currentpage
            if (tn_p > 1) qs.append("tn_p", `${tn_p}`)
            const pathname = temp.pathname.replace(/\/$/, "")
            const asPath = `${pathname}?${qs}`.replace(/\?$/, "")
            const url = new URL(`${temp.origin}${asPath}`)
            if (url.toString() === window.location.href) return
            window?.history.pushState({ ...window.history.state, as: asPath }, "", url)
        },
        [path]
    )

    const onChange = useCallback(
        (qs: string, scrollToTop = false) => {
            if (scrollToTop) window.scroll({ top: 0, behavior: "smooth" })
            setIsRedirect(false)
            const _qs = new URLSearchParams(qs)
            _qs.set("tn_ps", "32")
            fetch(_qs)
                .then((res) => pushHistoryState(res))
                .catch(() => {})
        },
        [fetch, pushHistoryState]
    )
    const total = response?.properties?.nrofitems || 0
    const init = useCallback(() => {
        const qs = new URLSearchParams(window.location.search)
        qs.set("tn_ps", "32")
        if (facets) {
            Object.entries(facets).forEach(([key, value]) => {
                qs.set(key, value)
            })
        }
        qs.set("tn_cid", categoryId)
        fetch(qs)
            .then((res) => pushHistoryState(res))
            .catch(() => {})
    }, [categoryId, facets, fetch, pushHistoryState])
    useEffect(() => {
        function onPop(e: PopStateEvent) {
            if (e.state.as === router.asPath) {
                init()
            }
        }

        window.addEventListener("popstate", onPop)
        return () => {
            window.removeEventListener("popstate", onPop)
        }
    }, [init, router])

    const session = useSession()
    useEffect(() => {
        if (session.status === "loading") return
        init()
    }, [init, session.status])

    useEffect(() => {
        if (router.isReady) {
            const qs = new URLSearchParams(window.location.search)
            const isRedirect = qs.has("redirect")
            if (isRedirect) {
                setIsRedirect(true)
                qs.delete("redirect")
                const pathname = window.location.pathname
                const newUrl = `${pathname}${qs.toString() ? "?" + qs.toString() : ""}`
                window?.history.pushState(window.history.state, "", newUrl)
            } else {
                setIsRedirect(false)
            }
        }
    }, [router])

    return {
        total,
        onChange,
        path,
        pending,
        response,
        loaded,
        getGroup,
        isRedirect,
        setIsRedirect,
    }
}

export const useFilters = (data: NavigationResponse) =>
    useMemo(() => {
        const pageUrl = data?.properties?.pageurl || ""
        return (
            data?.facets
                ?.filter((facet) => !["tree", "link"].includes(facet.facetsettings.selectiontype))
                ?.map((facet) => getSelectedAttributes(facet, pageUrl))
                ?.filter(typedBoolean) || []
        )
    }, [data])
